/**
 * Shape Drag Configuration
 *
 * Type-specific configuration for shape geometry dragging.
 * Defines how shapes should be handled during whole-geometry drag operations.
 *
 * @module hooks/drag/configs/shapeDragConfig
 */

import type { GeometryDragConfig } from '../useGeometryWholeDrag';
import { createShapeGeoJson } from '../../../components/map/GeometryRenderer/ShapeRenderer/utils/shapeGeoJsonHelpers';
import {
  SHAPE_SOURCE_PREFIX,
  SHAPE_HITBOX_PREFIX,
  RESIZE_HANDLE_SELECTOR,
} from '../../../constants/geometry';

/**
 * Creates configuration for shape geometry drag behavior
 *
 * Shape-specific behavior:
 * - Uses `shapePoints` array for coordinates
 * - Calculates center using simple offset of original center (shapes move as rigid bodies)
 * - NEEDS to track original center (used for offset calculation)
 * - Skips drag when clicking on resize handles (vertex/edge drag)
 *
 * @returns Configuration object for shape drag
 */
export const createShapeDragConfig = (): GeometryDragConfig => ({
  // MapLibre source prefix for shapes
  sourcePrefix: SHAPE_SOURCE_PREFIX,

  // MapLibre layer prefix for shape hitboxes (interactive layers)
  layerPrefix: SHAPE_HITBOX_PREFIX,

  // Skip drag when clicking on resize handles (allows vertex/edge drag)
  handleSelector: RESIZE_HANDLE_SELECTOR,

  // Extract shape points from geometry
  getPoints: (geometry) => geometry.shapePoints,

  // Create updated geometry with new shape points
  createUpdatedGeometry: (_originalGeometry, newPoints) => ({
    shapePoints: newPoints,
  }),

  // Calculate center using simple offset (shapes move as rigid bodies)
  // Uses original center + offset (more predictable for closed shapes)
  calculateCenter: (geometry, _newPoints, originalCenter, offset) => {
    if (originalCenter) {
      return {
        lat: originalCenter.lat + offset.lat,
        lng: originalCenter.lng + offset.lng,
      };
    }
    // Fallback to existing center if originalCenter not available
    return geometry.calculatedCenter;
  },

  // Create GeoJSON for MapLibre preview during drag
  createGeoJson: (geometry) => createShapeGeoJson({ geometry }),

  // Shape needs original center for offset calculation
  needsOriginalCenter: true,
});
